import React from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useLocationContext } from '../../hooks/useLocationContext';
import { useLocalGetLocationByIdQuery } from '../../store/api/localApi';
import CustomGoalsAdmin from '../../components/learning-goals/CustomGoalsAdmin';

/**
 * Org-scoped page for Aistéar learning goals.
 *
 * Access: LOCATION_ADMIN+ manage (add/deactivate custom goals); STAFF may VIEW
 * read-only (they tag observations against these goals, so they need to see the
 * catalogue). Everyone else is blocked. The management endpoints are enforced
 * server-side regardless — read-only here only tailors the UI.
 *
 * Org resolution: an ORG_ADMIN carries the org on their membership
 * (`activeOrganizationId`). A SUPER_ADMIN scoped into a location has NO org
 * membership and the `/loc/:locId/...` URL carries no org segment, so we fall
 * back to looking up the active location's `organizationId`. Without this, the
 * page wrongly showed "no access" for super-admins at location scope.
 */
const LearningGoalsPage: React.FC = () => {
  const { isAdmin, isStaff, activeOrganizationId } = useAuth();
  const { effectiveLocationId } = useLocationContext();

  // Only need the lookup when the org isn't already known from membership.
  const { data: location } = useLocalGetLocationByIdQuery(effectiveLocationId as string, {
    skip: !!activeOrganizationId || !effectiveLocationId,
  });

  const organizationId = activeOrganizationId || location?.organizationId;

  if (!isAdmin && !isStaff) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <p className="text-sm text-gray-500">You don't have access to learning goals.</p>
      </div>
    );
  }

  if (!organizationId) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <p className="text-sm text-gray-500">Resolving organisation…</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <CustomGoalsAdmin organizationId={organizationId} readOnly={!isAdmin} />
    </div>
  );
};

export default LearningGoalsPage;
